import React, { useEffect, useState } from "react";
import { Save, Phone, Mail, MapPin, Building2, Globe, Facebook, Instagram, Youtube, Plus, Trash2, ShoppingBag, Link, ShieldCheck, Send, Eye, EyeOff, CheckCircle2, AlertCircle, Loader } from "lucide-react";
import { toast } from "sonner";
import { api } from "../../services/api";

const DEFAULT_ADAM_HALL = [
    {
        title: "Cameo — Svetelná technika",
        description: "Scénické LED svetlá, wash, beam, spot a stroboskopy pre profesionálne pódiá. Objednajte originálne produkty Cameo cez naše zastúpenie za distribučné ceny.",
        url: "https://www.adamhall.com/shop/en/brands/cameo",
    },
    {
        title: "Flight Case Materiál",
        description: "Hliníkové profily, panty, rohové kódy, kolieska, uzávery a všetok materiál na výrobu a opravu flight cases presne na mieru vašich potrieb.",
        url: "https://www.adamhall.com/shop/en/flight-case-material",
    },
    {
        title: "Gravity — Stojany & príslušenstvo",
        description: "Prémiové mikrofónové a reproduktorové stojany, ramená, uchytenia a príslušenstvo pre profesionálnych umelcov aj fixné inštalácie.",
        url: "https://www.adamhall.com/shop/en/brands/gravity",
    },
];

const EMPTY = {
    company:      "",
    phone:        "",
    email:        "",
    address:      "",
    city:         "",
    area:         "",
    ico:          "",
    dic:          "",
    facebook_url:  "",
    instagram_url: "",
    youtube_url:   "",
    adam_hall_items: null,
    recaptcha_site_key:   "",
    recaptcha_secret_key: "",
};

function Field({ label, icon: Icon, value, onChange, placeholder, type = "text", hint }) {
    return (
        <div>
            <label className="font-mono text-[10px] uppercase tracking-wider text-zinc-500 mb-1.5 block">{label}</label>
            <div className="relative">
                {Icon && <Icon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-600 pointer-events-none" />}
                <input
                    type={type}
                    value={value}
                    onChange={e => onChange(e.target.value)}
                    placeholder={placeholder}
                    className={`w-full h-10 rounded-xl bg-nova-black/50 border border-white/8 text-white text-sm outline-none focus:border-nova-cyan/40 transition-colors ${Icon ? "pl-9 pr-3" : "px-3"}`}
                />
            </div>
            {hint && <p className="text-zinc-600 text-[10px] mt-1">{hint}</p>}
        </div>
    );
}

function Section({ title, children }) {
    return (
        <div className="bg-nova-surface/30 border border-white/8 rounded-2xl p-6 space-y-4">
            <h2 className="font-mono text-[10px] uppercase tracking-[0.2em] text-nova-cyan mb-5">{title}</h2>
            {children}
        </div>
    );
}

function AdamHallItemRow({ item, index, onChange, onRemove }) {
    const set = (k, v) => onChange(index, { ...item, [k]: v });
    return (
        <div className="border border-white/8 rounded-xl p-4 space-y-3 bg-nova-black/30">
            <div className="flex items-center justify-between mb-1">
                <span className="font-mono text-[10px] uppercase tracking-wider text-zinc-600">Položka {index + 1}</span>
                <button
                    onClick={() => onRemove(index)}
                    className="text-zinc-600 hover:text-red-400 transition-colors"
                    title="Odstrániť"
                >
                    <Trash2 className="w-3.5 h-3.5" />
                </button>
            </div>
            <div>
                <label className="font-mono text-[10px] uppercase tracking-wider text-zinc-500 mb-1.5 block">Názov</label>
                <input
                    value={item.title}
                    onChange={e => set("title", e.target.value)}
                    placeholder="Cameo — Svetelná technika"
                    className="w-full h-9 px-3 rounded-lg bg-nova-black/50 border border-white/8 text-white text-sm outline-none focus:border-nova-cyan/40 transition-colors"
                />
            </div>
            <div>
                <label className="font-mono text-[10px] uppercase tracking-wider text-zinc-500 mb-1.5 block">Popis</label>
                <textarea
                    value={item.description}
                    onChange={e => set("description", e.target.value)}
                    rows={2}
                    placeholder="Krátky popis produktovej kategórie..."
                    className="w-full px-3 py-2 rounded-lg bg-nova-black/50 border border-white/8 text-white text-sm outline-none focus:border-nova-cyan/40 transition-colors resize-none"
                />
            </div>
            <div>
                <label className="font-mono text-[10px] uppercase tracking-wider text-zinc-500 mb-1.5 block">URL odkaz</label>
                <div className="relative">
                    <Link className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-zinc-600 pointer-events-none" />
                    <input
                        value={item.url}
                        onChange={e => set("url", e.target.value)}
                        placeholder="https://www.adamhall.com/shop/..."
                        className="w-full h-9 pl-8 pr-3 rounded-lg bg-nova-black/50 border border-white/8 text-white text-sm outline-none focus:border-nova-cyan/40 transition-colors"
                    />
                </div>
            </div>
        </div>
    );
}

const SMTP_EMPTY = { host: "", port: 587, user: "", password: "", notify_email: "", use_ssl: false };

function SmtpSection() {
    const [cfg, setCfg]         = useState(SMTP_EMPTY);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving]   = useState(false);
    const [testing, setTesting] = useState(false);
    const [showPass, setShowPass] = useState(false);
    const [testResult, setTestResult] = useState(null); // null | "ok" | "error"
    const [testMsg, setTestMsg] = useState("");

    useEffect(() => {
        api.getSmtpSettings()
            .then(d => setCfg({ ...SMTP_EMPTY, ...d }))
            .catch(() => {})
            .finally(() => setLoading(false));
    }, []);

    const set = (k, v) => { setCfg(f => ({ ...f, [k]: v })); setTestResult(null); };

    const save = async () => {
        setSaving(true);
        try {
            await api.saveSmtpSettings(cfg);
            toast.success("SMTP nastavenia uložené");
        } catch (e) {
            toast.error(e.message);
        } finally {
            setSaving(false);
        }
    };

    const test = async () => {
        setTesting(true);
        setTestResult(null);
        try {
            await api.saveSmtpSettings(cfg);
            const res = await api.testSmtp();
            setTestResult("ok");
            setTestMsg(`Testovací email odoslaný na ${res.sent_to}`);
        } catch (e) {
            setTestResult("error");
            setTestMsg(e.message);
        } finally {
            setTesting(false);
        }
    };

    if (loading) return <div className="h-32 bg-nova-surface/20 rounded-xl animate-pulse" />;

    const presets = [
        { label: "Gmail", host: "smtp.gmail.com", port: 587, ssl: false },
        { label: "Outlook", host: "smtp.office365.com", port: 587, ssl: false },
        { label: "SendGrid", host: "smtp.sendgrid.net", port: 587, ssl: false },
        { label: "Mailtrap", host: "sandbox.smtp.mailtrap.io", port: 2525, ssl: false },
    ];

    return (
        <div className="bg-nova-surface/30 border border-white/8 rounded-2xl p-6 space-y-4">
            <div className="flex items-center justify-between">
                <h2 className="font-mono text-[10px] uppercase tracking-[0.2em] text-nova-cyan">/ Email & SMTP</h2>
                <div className="flex gap-2">
                    {presets.map(p => (
                        <button
                            key={p.label}
                            type="button"
                            onClick={() => setCfg(f => ({ ...f, host: p.host, port: p.port, use_ssl: p.ssl }))}
                            className="px-2 py-1 rounded text-[10px] font-mono border border-white/10 text-zinc-500 hover:text-white hover:border-white/25 transition-colors"
                        >
                            {p.label}
                        </button>
                    ))}
                </div>
            </div>

            <p className="text-zinc-500 text-xs -mt-1">
                Emailové notifikácie pri novom dopyte alebo rezervácii. Pre Gmail použite{" "}
                <a href="https://myaccount.google.com/apppasswords" target="_blank" rel="noopener noreferrer" className="text-nova-cyan hover:underline">
                    App Password
                </a>{" "}
                (nie bežné heslo).
            </p>

            <div className="grid grid-cols-2 gap-4">
                <div>
                    <label className="font-mono text-[10px] uppercase tracking-wider text-zinc-500 mb-1.5 block">SMTP Host</label>
                    <input value={cfg.host} onChange={e => set("host", e.target.value)}
                        placeholder="smtp.gmail.com"
                        className="w-full h-10 px-3 rounded-xl bg-nova-black/50 border border-white/8 text-white text-sm outline-none focus:border-nova-cyan/40 transition-colors" />
                </div>
                <div>
                    <label className="font-mono text-[10px] uppercase tracking-wider text-zinc-500 mb-1.5 block">Port</label>
                    <div className="flex gap-2 items-center">
                        <input value={cfg.port} onChange={e => set("port", parseInt(e.target.value) || 587)}
                            type="number" placeholder="587"
                            className="w-full h-10 px-3 rounded-xl bg-nova-black/50 border border-white/8 text-white text-sm outline-none focus:border-nova-cyan/40 transition-colors" />
                        <label className="flex items-center gap-1.5 shrink-0 cursor-pointer">
                            <input type="checkbox" checked={cfg.use_ssl} onChange={e => set("use_ssl", e.target.checked)}
                                className="accent-nova-cyan" />
                            <span className="text-zinc-400 text-xs font-mono">SSL</span>
                        </label>
                    </div>
                </div>
            </div>

            <div>
                <label className="font-mono text-[10px] uppercase tracking-wider text-zinc-500 mb-1.5 block">Používateľ (email odosielateľa)</label>
                <input value={cfg.user} onChange={e => set("user", e.target.value)}
                    type="email" placeholder="info@kbmusic.sk"
                    className="w-full h-10 px-3 rounded-xl bg-nova-black/50 border border-white/8 text-white text-sm outline-none focus:border-nova-cyan/40 transition-colors" />
            </div>

            <div>
                <label className="font-mono text-[10px] uppercase tracking-wider text-zinc-500 mb-1.5 block">Heslo / App Password</label>
                <div className="relative">
                    <input value={cfg.password} onChange={e => set("password", e.target.value)}
                        type={showPass ? "text" : "password"} placeholder="••••••••••••"
                        className="w-full h-10 pl-3 pr-10 rounded-xl bg-nova-black/50 border border-white/8 text-white text-sm outline-none focus:border-nova-cyan/40 transition-colors" />
                    <button type="button" onClick={() => setShowPass(v => !v)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-500 hover:text-white transition-colors">
                        {showPass ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                </div>
            </div>

            <div>
                <label className="font-mono text-[10px] uppercase tracking-wider text-zinc-500 mb-1.5 block">Notifikačný email (príjemca)</label>
                <input value={cfg.notify_email} onChange={e => set("notify_email", e.target.value)}
                    type="email" placeholder="info@kbmusic.sk"
                    className="w-full h-10 px-3 rounded-xl bg-nova-black/50 border border-white/8 text-white text-sm outline-none focus:border-nova-cyan/40 transition-colors" />
                <p className="text-zinc-600 text-[10px] mt-1">Sem prídu notifikácie o nových dopytoch a rezerváciách. Ak prázdne, použije sa používateľ.</p>
            </div>

            {testResult && (
                <div className={`flex items-start gap-2 p-3 rounded-lg text-sm ${testResult === "ok" ? "bg-emerald-500/10 border border-emerald-500/20 text-emerald-400" : "bg-red-500/10 border border-red-500/20 text-red-400"}`}>
                    {testResult === "ok"
                        ? <CheckCircle2 className="w-4 h-4 shrink-0 mt-0.5" />
                        : <AlertCircle className="w-4 h-4 shrink-0 mt-0.5" />}
                    <span className="text-xs">{testMsg}</span>
                </div>
            )}

            <div className="flex gap-3 pt-1">
                <button onClick={test} disabled={testing || saving}
                    className="flex items-center gap-2 px-4 py-2 rounded-xl border border-white/15 text-zinc-300 text-sm hover:bg-white/5 transition-colors disabled:opacity-50">
                    {testing ? <Loader className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                    {testing ? "Testujem…" : "Otestovať & uložiť"}
                </button>
                <button onClick={save} disabled={saving || testing}
                    className="flex items-center gap-2 px-4 py-2 rounded-xl bg-nova-cyan text-black font-semibold text-sm hover:bg-nova-cyan/90 transition-colors disabled:opacity-50">
                    {saving ? <Loader className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                    {saving ? "Ukladám…" : "Uložiť"}
                </button>
            </div>
        </div>
    );
}

export default function AdminSettings() {
    const [form, setForm]       = useState(EMPTY);
    const [adamItems, setAdamItems] = useState(DEFAULT_ADAM_HALL);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving]   = useState(false);

    useEffect(() => {
        api.getSettings()
            .then(d => {
                setForm({ ...EMPTY, ...d });
                if (d.adam_hall_items && d.adam_hall_items.length > 0) {
                    setAdamItems(d.adam_hall_items);
                }
            })
            .catch(() => toast.error("Nepodarilo sa načítať nastavenia"))
            .finally(() => setLoading(false));
    }, []);

    const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

    const updateAdamItem = (index, updated) => {
        setAdamItems(prev => prev.map((it, i) => i === index ? updated : it));
    };
    const removeAdamItem = (index) => {
        setAdamItems(prev => prev.filter((_, i) => i !== index));
    };
    const addAdamItem = () => {
        setAdamItems(prev => [...prev, { title: "", description: "", url: "" }]);
    };

    const save = async () => {
        setSaving(true);
        try {
            await api.saveSettings({ ...form, adam_hall_items: adamItems });
            toast.success("Nastavenia uložené");
        } catch (e) {
            toast.error(e.message);
        } finally {
            setSaving(false);
        }
    };

    if (loading) return (
        <div className="space-y-4 animate-pulse">
            {[1,2,3].map(n => <div key={n} className="h-40 bg-nova-surface/40 rounded-2xl" />)}
        </div>
    );

    return (
        <div className="max-w-2xl space-y-6">
            <div className="flex items-center justify-between mb-8">
                <div>
                    <h1 className="font-heading text-2xl font-black text-white tracking-tight">Nastavenia webu</h1>
                    <p className="text-zinc-500 text-sm mt-1">Kontaktné údaje, sídlo a sekcie zobrazované na webe.</p>
                </div>
                <button
                    onClick={save}
                    disabled={saving}
                    className="flex items-center gap-2 px-5 py-2.5 rounded-xl bg-nova-cyan text-nova-black font-semibold text-sm hover:bg-nova-cyan/90 transition-colors disabled:opacity-50"
                >
                    <Save className="w-4 h-4" />
                    {saving ? "Ukladám…" : "Uložiť"}
                </button>
            </div>

            <Section title="/ Spoločnosť">
                <Field label="Názov spoločnosti" icon={Building2} value={form.company} onChange={v => set("company", v)} placeholder="KB MUSIC s.r.o." />
                <div className="grid grid-cols-2 gap-4">
                    <Field label="IČO" value={form.ico} onChange={v => set("ico", v)} placeholder="12345678" />
                    <Field label="DIČ" value={form.dic} onChange={v => set("dic", v)} placeholder="SK12345678" />
                </div>
            </Section>

            <Section title="/ Kontakt">
                <Field label="Telefón" icon={Phone} value={form.phone} onChange={v => set("phone", v)} placeholder="+421 905 000 000" type="tel" />
                <Field label="Email" icon={Mail} value={form.email} onChange={v => set("email", v)} placeholder="info@kbmusic.sk" type="email" />
            </Section>

            <Section title="/ Sídlo a pôsobnosť">
                <Field label="Ulica a číslo" icon={MapPin} value={form.address} onChange={v => set("address", v)} placeholder="Hlavná 1" />
                <Field label="Mesto / PSČ" value={form.city} onChange={v => set("city", v)} placeholder="831 01 Bratislava" />
                <Field label="Pôsobnosť" icon={Globe} value={form.area} onChange={v => set("area", v)} placeholder="Celá SR & ČR" hint="Zobrazuje sa v sekcii Kontakt na úvodnej stránke." />
            </Section>

            <Section title="/ Sociálne siete">
                <Field label="Facebook URL" icon={Facebook} value={form.facebook_url} onChange={v => set("facebook_url", v)} placeholder="https://facebook.com/kbmusic" />
                <Field label="Instagram URL" icon={Instagram} value={form.instagram_url} onChange={v => set("instagram_url", v)} placeholder="https://instagram.com/kbmusic" />
                <Field label="YouTube URL" icon={Youtube} value={form.youtube_url} onChange={v => set("youtube_url", v)} placeholder="https://youtube.com/@kbmusic" />
            </Section>

            <Section title="/ reCAPTCHA v3">
                <p className="text-zinc-500 text-xs -mt-2 mb-2">
                    Chráni kontaktný formulár pred botmi. Kľúče získate na{" "}
                    <a href="https://www.google.com/recaptcha/admin" target="_blank" rel="noopener noreferrer" className="text-nova-cyan hover:underline">
                        google.com/recaptcha
                    </a>{" "}
                    (vyberte reCAPTCHA v3).
                </p>
                <Field
                    label="Site Key (verejný)"
                    icon={ShieldCheck}
                    value={form.recaptcha_site_key}
                    onChange={v => set("recaptcha_site_key", v)}
                    placeholder="6Lc..."
                    hint="Vkladá sa do frontendu — viditeľný."
                />
                <Field
                    label="Secret Key (tajný)"
                    icon={ShieldCheck}
                    value={form.recaptcha_secret_key}
                    onChange={v => set("recaptcha_secret_key", v)}
                    placeholder="6Lc..."
                    hint="Používa ho backend na overenie — nikdy nezverejňujte."
                />
            </Section>

            <SmtpSection />

            <Section title="/ Adam Hall — Produkty & distribúcia">
                <p className="text-zinc-500 text-xs -mt-2 mb-2">
                    Tieto položky sa zobrazujú v sekcii „Nakúpte profesionálne vybavenie" na úvodnej stránke.
                    Môžete ich upravovať, pridávať a odstraňovať.
                </p>
                <div className="space-y-3">
                    {adamItems.map((item, i) => (
                        <AdamHallItemRow
                            key={i}
                            item={item}
                            index={i}
                            onChange={updateAdamItem}
                            onRemove={removeAdamItem}
                        />
                    ))}
                </div>
                <button
                    onClick={addAdamItem}
                    className="mt-2 flex items-center gap-2 px-4 py-2 rounded-xl border border-white/10 text-zinc-400 hover:text-white hover:border-white/25 text-sm transition-colors"
                >
                    <Plus className="w-4 h-4" />
                    Pridať položku
                </button>
            </Section>
        </div>
    );
}
